/**
 * Renders a collapsible, variant-aware add-on selector for a single add-on type.
 * Fires `addOn:added`, `addOn:removed`, and `addOn:clear` events for consumption by `<addons-manager>`.
 */
if (!customElements.get('product-addon')) {

  /**
   * Global cache for expand/collapse state, keyed by `addOnType`.
   * Persists across component re-mounts (e.g. variant-driven section reloads).
   */
  window.ProductAddon = window.ProductAddon || { expanded: {} };

  /**
   * Renders a collapsible add-on selector for a single add-on type.
   * Fires `addOn:added`, `addOn:removed`, and `addOn:clear` custom events
   * for consumption by `addons-manager`.
   */
  class ComponentProductAddOn extends HTMLElement {
    constructor() {
      super();
      this.id = this.dataset.id;

      this.addOnType = this.dataset.addonType;
      this.bundleType = this.dataset.bundleType;
      this.isHighVariants = this.dataset.isHighVariants === 'true';
      this.currentVariantId = parseInt(this.dataset.currentVariantId);

      this.market = this.dataset.market;

      this.mainElement = this.closest('.product-main-element')
      this.expandButton = this.querySelector('.btn-expand');
      this.panel = this.querySelector('.addons-panel');

      this.productAddonsJsonElement = this.querySelector('[type="application/json"][data-product-addons-json]');
      this.productAddonsJsonData = JSON.parse(this.productAddonsJsonElement.textContent);

      this.addOnsGroup = this.querySelector('.addon-group');
      this.statusRegion = this.querySelector('[data-addon-status]');

      this.isInitialized = false;
      this.variantChangeHandler = this.onVariantChange.bind(this)

      this.init()
    }

    init() {
      this.updateVariant(this.currentVariantId)
      if (!this.isHighVariants) {
         if (this.mainElement) {
           this.mainElement.addEventListener('variantChange', this.variantChangeHandler)
         }
      }

      this.isInitialized = true;
      this.initExpander();
      this.restoreExpandState();
      this.dispatchEvent(new CustomEvent('addOn:clear'));
      this.mainElement?.dispatchEvent(new CustomEvent('shippingDate:update', { detail: { adjustLeadTime: 0, adjustBufferDays:0 } }))
    }

    updateVariant(variantId) {
      this.currentVariant = this.productAddonsJsonData.find(variant => variant.variantId === variantId);
      this.buildOptions()
    }

    disconnectedCallback() {
      this.mainElement?.removeEventListener('variantChange', this.variantChangeHandler)
    }

    onVariantChange(event) {
      this.updateVariant(event.detail.variant.id)
    }

    buildOptions() {
      this.addOnsGroup.querySelectorAll('.item').forEach(el => el.remove());
      if (!this.currentVariant || !this.currentVariant.addOns.length) {
        this.closest('.add-on').style.setProperty('display', 'none', 'important')
        return
      } else {
        this.closest('.add-on').style.removeProperty('display')
      }
      this.style.setProperty('--num-items', this.currentVariant.addOns.length)
      this.addOnsGroup.append(...this.currentVariant
        .addOns
        .map((addOn, indexAddOn) => {
          const item = document.createElement('div')
          item.classList.add('item')
          item.innerHTML = `
            <input type="checkbox"
                  class="addon-checkbox"
                  name="addon-${this.addOnType}-${this.id}"
                  id="addon-${this.id}-${indexAddOn}"
                  value="${addOn.variantId}">
            <label for="addon-${this.id}-${indexAddOn}" class="btn-addon">
              <span class="addon-title">${addOn.title} (${addOn.formatedPrice})</span></label>
          `;
          return item;
      }))

      if (this.isInitialized && this.statusRegion) {
        const count = this.currentVariant.addOns.length;
        this.statusRegion.textContent = `${count} option${count !== 1 ? 's' : ''} available`;
      }
      this.initCheckboxes();
    }

    initExpander() {
      this.expandButton.addEventListener('click', this.onToggle.bind(this));
    }

    /** Restores open/closed state from `window.ProductAddon.expanded` on mount. */
    restoreExpandState() {
      const open = window.ProductAddon.expanded[this.addOnType] || false;
      if (open) {
        this.expandButton.setAttribute('aria-expanded', 'true');
        this.classList.add('open');
      }
    }

    onToggle(event) {
      event.preventDefault();
      const open = this.expandButton.getAttribute('aria-expanded') !== 'true';
      this.expandButton.setAttribute('aria-expanded', String(open));
      this.classList.toggle('open', open);
      window.ProductAddon.expanded[this.addOnType] = open;
    }

    initCheckboxes() {
      this.querySelectorAll('.addon-checkbox').forEach(checkbox => {
        checkbox.addEventListener('change', this.onAddonChange.bind(this));
      });
    }

    /**
     * Handles checkbox changes. Enforces single selection: when checking a new
     * option the previously checked one is unchecked and `addOn:removed` is fired
     * for it first. Then fires `addOn:added` or `addOn:removed` for the target.
     */
    onAddonChange(event) {

      const findVariant = (value) => this.currentVariant.addOns.find(v => parseInt(v.id) === parseInt(value));

      // When switching: the loser is the previously checked box.
      // When deselecting: the loser is event.target itself.
      const previouslyChecked = [...this.querySelectorAll('.addon-checkbox')].find(cb => cb !== event.target && cb.checked);
      const removedCheckbox = event.target.checked ? previouslyChecked : event.target;

      if (removedCheckbox) {
        removedCheckbox.checked = false;
        const removedVariant = findVariant(removedCheckbox.value);
        if (removedVariant) this.dispatchEvent(new CustomEvent('addOn:removed', { detail: { addOnType: this.addOnType, bundleType: this.bundleType, variant: removedVariant } }));
        this.mainElement?.dispatchEvent(new CustomEvent('shippingDate:update', { detail: { adjustLeadTime: 0, adjustBufferDays:0 } }))
      }

      if (event.target.checked) {
        const addedVariant = findVariant(event.target.value);
        if (addedVariant) {
          this.dispatchEvent(new CustomEvent('addOn:added', { detail: { addOnType: this.addOnType, bundleType: this.bundleType, variant: addedVariant, market: this.market } }));
          this.mainElement?.dispatchEvent(new CustomEvent('shippingDate:update', { detail: { adjustLeadTime: addedVariant.lead_time, adjustBufferDays: addedVariant.buffer_days } }))
        }
      }
    }
  }

  customElements.define('product-addon', ComponentProductAddOn);
}
